<template>
  <div ref="mapContainer" class="map-container"></div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { Config } from '@/config';

mapboxgl.accessToken = Config.mapbox.publicKey;

export default {
  name: 'BaseMap',
  props: {
    center: {
      type: Object,
      required: true,
    },
    markers: {
      type: Array,
      default: () => [],
    },
    routeCoordinates: {
      type: Array,
      default: () => [],
    },
    zoom: {
      type: Number,
      default: 8,
    },
  },
  data() {
    return {
      map: null,
      isMapLoaded: false,
      markerInstances: [],
    };
  },
  mounted() {
    const map = new mapboxgl.Map({
      container: this.$refs.mapContainer,
      style: 'mapbox://styles/moikiki/cm7alnbqt003r01s433b11yvj', // Replace with the preferred map style
      center: [this.center.longitude, this.center.latitude],
      zoom: this.zoom,
    });

    this.map = map;

    this.updateMarkers();

    this.map.on('load', this.onMapLoad);
  },
  beforeDestroy() {
    this.markerInstances.forEach((marker) => marker.remove());
    this.map.remove();
    this.map = null;
    this.isMapLoaded = false;
  },
  methods: {
    markerPoints() {
      return this.markers
        .filter(
          (marker) =>
            marker &&
            marker.longitude != null &&
            marker.latitude != null &&
            String(marker.longitude).trim() &&
            String(marker.latitude).trim(),
        )
        .map((marker) => [Number(marker.longitude), Number(marker.latitude)])
        .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]));
    },
    updateMarkers() {
      if (!this.map) return;
      this.markerInstances.forEach((marker) => marker.remove());
      this.markerInstances = this.markerPoints().map((point) => new mapboxgl.Marker().setLngLat(point).addTo(this.map));
    },
    onMapLoad() {
      this.isMapLoaded = true;
      this.addRouteToMap();
      this.fitVisibleLocations();
    },
    fitVisibleLocations() {
      if (!this.isMapLoaded) return;
      const points = [...this.markerPoints(), ...this.routeCoordinates].filter(
        (point) => point && point.length >= 2 && Number.isFinite(point[0]) && Number.isFinite(point[1]),
      );
      if (points.length === 1) {
        this.map.setCenter(points[0]);
        return;
      }
      if (points.length < 2) return;
      const bounds = points.reduce(
        (result, point) => result.extend(point),
        new mapboxgl.LngLatBounds(points[0], points[0]),
      );
      this.map.fitBounds(bounds, { padding: 35, maxZoom: this.zoom });
    },
    addRouteToMap() {
      if (!this.isMapLoaded) return;
      if (!this.routeCoordinates || this.routeCoordinates.length === 0) {
        if (this.map.getLayer('route')) this.map.removeLayer('route');
        if (this.map.getSource('route')) this.map.removeSource('route');
        return;
      }
      const geojson = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: this.routeCoordinates,
        },
      };
      // if the route already exists on the map, we'll reset it using setData
      if (this.map.getSource('route')) {
        this.map.getSource('route').setData(geojson);
      }
      // otherwise, we'll make a new request
      else {
        this.map.addLayer({
          id: 'route',
          type: 'line',
          source: {
            type: 'geojson',
            data: geojson,
          },
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#3887be',
            'line-width': 5,
            'line-opacity': 0.75,
          },
        });
      }
    },
  },
  watch: {
    markers: {
      deep: true,
      handler() {
        this.updateMarkers();
        this.fitVisibleLocations();
      },
    },
    center: {
      deep: true,
      handler(center) {
        if (!this.map || !center) return;
        this.map.setCenter([Number(center.longitude), Number(center.latitude)]);
        this.fitVisibleLocations();
      },
    },
    routeCoordinates() {
      this.addRouteToMap();
      this.fitVisibleLocations();
    },
  },
};
</script>

<style scoped lang="scss">
.map-container {
}
</style>
